import type { ReactNode } from 'react';

export interface Column<T> { key: string; header: string; render?: (row: T) => ReactNode; numeric?: boolean }

/**
 * Responsive table: a real <table> on wide screens; under 640 px each row becomes a stacked card (labels
 * from data-label), so nothing overflows horizontally at 360 px (QM-NC02-002 "a table overflows").
 */
export function DataTable<T extends { id: string }>({ caption, columns, rows, captionHidden = true }: { caption: string; columns: Column<T>[]; rows: T[]; captionHidden?: boolean }) {
  return (
    <div className="qm-table-wrap">
      <table className="qm-table">
        <caption className={captionHidden ? 'qm-sr-only' : undefined}>{caption}</caption>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} scope="col" data-numeric={c.numeric ? 'true' : undefined}>{c.header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              {columns.map((c) => (
                <td key={c.key} data-label={c.header} data-numeric={c.numeric ? 'true' : undefined}>
                  {c.render ? c.render(r) : String((r as Record<string, unknown>)[c.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'accent' | 'warning' | 'error' | 'success'; children: ReactNode }) {
  return <span className="qm-badge" data-tone={tone}>{children}</span>;
}

/** Empty state with a restrained lavender disc/ring as structural art (decorative, aria-hidden). */
export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="qm-empty">
      <div className="qm-empty__body">
        <p className="qm-empty__title">{title}</p>
        {children}
      </div>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return <p className="qm-error" role="alert">{message}</p>;
}

export function Loading({ label }: { label: string }) {
  return <p className="qm-loading" role="status" aria-live="polite">{label}</p>;
}

/** Metric: mono uppercase micro-label over an oversized tight number. */
export function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="qm-stat">
      <p className="qm-stat__label">{label}</p>
      <p className="qm-stat__value">{value}</p>
    </div>
  );
}
